import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { formatPaymentMode } from '../../lib/utils';

const todayStr = () => new Date().toISOString().split('T')[0];
const threeMonthsBack = () => { const d = new Date(); d.setMonth(d.getMonth() - 3); return d.toISOString().split('T')[0]; };

const CustomerDetailsCard = ({
  customerRef, customerSearch, setCustomerSearch, setSelectedCustomer,
  customerDropdownOpen, setCustomerDropdownOpen, filteredCustomers, selectCustomer,
  occasion, setOccasion, eventId, setEventId, events,
  newEventName, setNewEventName, handleAddEvent, addingEvent,
  orderDate, setOrderDate, dispatchDate, setDispatchDate, channel, setChannel,
  selectedAgent, setSelectedAgent, agents, selectedDistributor, setSelectedDistributor, distributors,
  transportName, setTransportName, soNotes, setSoNotes, custObj,
}) => (
  <Card>
    <CardContent className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* Customer search */}
        <div className="relative" ref={customerRef}>
          <Label className="text-xs font-semibold text-gray-600">Customer *</Label>
          <Input value={customerSearch}
            onChange={e => { setCustomerSearch(e.target.value); setCustomerDropdownOpen(!!e.target.value.trim()); if (!e.target.value) setSelectedCustomer(''); }}
            placeholder="Search customer..." className="h-9 text-sm" data-testid="so-customer-search"
          />
          {customerDropdownOpen && filteredCustomers.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filteredCustomers.map(c => (
                <div key={c.id} className="px-3 py-2 hover:bg-sky-50 cursor-pointer text-sm"
                  onClick={() => selectCustomer(c)} data-testid={`so-cust-option-${c.id}`}>
                  <span className="font-medium">{c.company_name}</span>
                  {c.city && <span className="text-gray-400 ml-2">({c.city})</span>}
                  {c.discount_percent > 0 && <Badge className="ml-2 bg-green-100 text-green-700 text-xs">{c.discount_percent}% disc</Badge>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs font-semibold text-gray-600">Mode of Order *</Label>
          <select value={occasion} onChange={e => setOccasion(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm" data-testid="so-occasion">
            <option value="">Select</option>
            <option value="door_to_door">Door to Door</option>
            <option value="exhibition">Exhibition</option>
            <option value="office_visit">Office Visit</option>
            <option value="open_order">Open Order</option>
            <option value="whatsapp">Whatsapp</option>
          </select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-gray-600">Event <span className="text-gray-400 font-normal">(optional)</span></Label>
          <select value={eventId} onChange={e => setEventId(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm" data-testid="so-event">
            <option value="">— None —</option>
            {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
          </select>
          <div className="flex gap-1 mt-1">
            <Input
              className="h-7 text-xs flex-1"
              placeholder="New event name…"
              value={newEventName}
              onChange={e => setNewEventName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddEvent(); } }}
              data-testid="so-new-event-input"
            />
            <Button
              size="sm" variant="outline"
              className="h-7 px-2 text-xs"
              onClick={handleAddEvent}
              disabled={addingEvent || !newEventName.trim()}
              data-testid="so-add-event-btn"
            >+ Add</Button>
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold text-gray-600">Order Date *</Label>
          <Input type="date" value={orderDate} max={todayStr()} min={threeMonthsBack()} onChange={e => setOrderDate(e.target.value)} className="h-9 text-sm" data-testid="so-order-date" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-gray-600">Dispatch Date</Label>
          <Input type="date" value={dispatchDate} onChange={e => setDispatchDate(e.target.value)} className="h-9 text-sm" data-testid="so-dispatch-date" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-3">
        <div>
          <Label className="text-xs font-semibold text-gray-600">Channel</Label>
          <select value={channel} onChange={e => { setChannel(e.target.value); if (e.target.value !== 'via_agent') setSelectedAgent(''); if (e.target.value !== 'via_distributor') setSelectedDistributor(''); }} className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm" data-testid="so-channel">
            <option value="">Select</option>
            <option value="direct">Direct</option>
            <option value="via_agent">Via Agent</option>
            <option value="via_distributor">Via Distributor</option>
          </select>
        </div>
        {channel === 'via_agent' && (
          <div>
            <Label className="text-xs font-semibold text-gray-600">Agent</Label>
            <select value={selectedAgent} onChange={e => setSelectedAgent(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm" data-testid="so-agent">
              <option value="">Select Agent</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        )}
        {channel === 'via_distributor' && (
          <div>
            <Label className="text-xs font-semibold text-gray-600">Distributor</Label>
            <select value={selectedDistributor} onChange={e => setSelectedDistributor(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm" data-testid="so-distributor">
              <option value="">Select Distributor</option>
              {distributors.map(d => <option key={d.id} value={d.id}>{d.company_name}</option>)}
            </select>
          </div>
        )}
        <div>
          <Label className="text-xs font-semibold text-gray-600">Transport</Label>
          <Input value={transportName} onChange={e => setTransportName(e.target.value)} placeholder="Transport name" className="h-9 text-sm" data-testid="so-transport" />
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs font-semibold text-gray-600">Notes</Label>
          <Textarea value={soNotes} onChange={e => setSoNotes(e.target.value)} placeholder="Order notes..." rows={2} className="text-sm min-h-[60px] resize-y" data-testid="so-notes" />
        </div>
      </div>
      {custObj && (
        <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 text-xs" data-testid="so-customer-summary">
          <div><span className="text-gray-400 block">Channel</span><strong className="text-gray-700 capitalize">{channel.replace(/_/g, ' ')}</strong></div>
          <div><span className="text-gray-400 block">WSP Type</span><strong className="text-gray-700 capitalize">{custObj.wsp_type || 'Local'}</strong></div>
          {custObj.discount_percent > 0 && <div><span className="text-gray-400 block">Discount</span><strong className="text-green-700">{custObj.discount_percent}%</strong></div>}
          <div><span className="text-gray-400 block">Payment Mode</span><strong className="text-gray-700">{custObj.payment_mode ? formatPaymentMode(custObj.payment_mode) : '—'}</strong>{custObj.payment_mode === 'credit' && custObj.credit_days && <span className="text-gray-400 ml-1">({custObj.credit_days}d)</span>}</div>
          <div><span className="text-gray-400 block">Transport</span><strong className="text-gray-700">{custObj.transport_preference || '—'}</strong></div>
          {channel === 'via_distributor' && <div><span className="text-gray-400 block">Distributor</span><strong className="text-gray-700">{distributors.find(d => d.id === selectedDistributor)?.company_name || '—'}</strong></div>}
          {channel === 'via_agent' && <div><span className="text-gray-400 block">Agent</span><strong className="text-gray-700">{agents.find(a => a.id === selectedAgent)?.name || '—'}</strong></div>}
        </div>
      )}
    </CardContent>
  </Card>
);

export default CustomerDetailsCard;
